import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { Close } from "@mui/icons-material";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { releaseFocusFromBackground } from "../../utils/modalFocus";

const ELEVATED_ENTER_EASING = "cubic-bezier(0.16, 1, 0.3, 1)";

interface ActionModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Renders below scrollable content; on mobile full-screen modals this stays pinned above the safe area. */
  footer?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  headerStyle?: "default" | "light" | "minimal";
  disableBackdropClick?: boolean; // Prevent closing on backdrop click
  /** Tighter padding under the header so the first controls sit closer to the title bar. */
  denseContent?: boolean;
  /** Merged after default / dense scroll-area padding (e.g. wider horizontal inset on phones). */
  contentSx?: SxProps<Theme>;
  /** Merged after default footer padding. */
  footerSx?: SxProps<Theme>;
  /** Optional Paper overrides for one-off sizing/layout tweaks. */
  paperSx?: SxProps<Theme>;
  /** Merged into DialogContent (scroll area) for per-modal height tweaks. */
  dialogContentSx?: SxProps<Theme>;
  /** `elevated` — cinematic fade + scale (desktop) or slide-up (mobile). */
  enterAnimation?: "default" | "elevated";
}

const ActionModal: React.FC<ActionModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  maxWidth = "md",
  headerStyle = "default",
  disableBackdropClick = false,
  denseContent = false,
  contentSx,
  footerSx,
  paperSx,
  dialogContentSx,
  enterAnimation = "default",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const useElevatedEnter = enterAnimation === "elevated" && !prefersReducedMotion;
  useBodyScrollLock(open);

  useEffect(() => {
    if (open) {
      releaseFocusFromBackground();
    }
  }, [open]);

  const getHeaderStyles = () => {
    switch (headerStyle) {
      case "light":
        return {
          background:
            `linear-gradient(to right, ${theme.palette.primary.main} 0%, ${alpha(
              theme.palette.primary.main,
              0.8
            )} 100%)`,
          color: "common.white",
          titleWeight: 500,
          closeOnDark: false,
        };
      case "minimal":
        return {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          titleWeight: 600,
          closeOnDark: true,
        };
      default:
        return {
          background:
            `linear-gradient(to right, ${theme.palette.primary.main} 0%, ${alpha(
              theme.palette.primary.main,
              0.8
            )} 100%)`,
          color: "common.white",
          titleWeight: 300,
          closeOnDark: false,
        };
    }
  };

  const headerStyles = getHeaderStyles();

  const bodyPaddingSx = denseContent
    ? { p: { xs: 1, sm: 2 }, pt: { xs: 0.5, sm: 1 } }
    : { p: { xs: 1, sm: 3 }, pt: { xs: 1, sm: 2 } };

  const footerPaddingBaseSx: SxProps<Theme> = {
    flexShrink: 0,
    borderTop: "1px solid",
    borderColor: "divider",
    bgcolor: "grey.50",
    px: { xs: 2, sm: 3 },
    py: { xs: 1.5, sm: 2 },
    pb: {
      xs: "calc(12px + env(safe-area-inset-bottom, 0px))",
      sm: 2,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={disableBackdropClick ? undefined : onClose}
      disableEscapeKeyDown={disableBackdropClick}
      disableRestoreFocus
      disableScrollLock
      maxWidth={maxWidth}
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      slotProps={
        useElevatedEnter
          ? {
              backdrop: {
                sx: {
                  backgroundColor: alpha(theme.palette.common.black, 0.55),
                },
              },
            }
          : undefined
      }
      sx={{
        "& .MuiDialog-container": {
          alignItems: isMobile ? "stretch" : "center",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
          maxHeight: isMobile ? "100dvh" : "95vh",
          height: isMobile ? "100dvh" : "auto",
          m: isMobile ? 0 : undefined,
          display: "flex",
          flexDirection: "column",
          ...(isMobile && {
            position: "fixed",
            inset: 0,
          }),
          ...(useElevatedEnter &&
            open && {
              transformOrigin: "center center",
              "@keyframes actionModalElevatedEnter": {
                from: { opacity: 0, transform: "scale(0.98)" },
                to: { opacity: 1, transform: "none" },
              },
              animation: `actionModalElevatedEnter 0.38s ${ELEVATED_ENTER_EASING} both`,
            }),
          ...(paperSx || {}),
        },
      }}
    >
      {/* Modern Header */}
      <Box
        sx={{
          background: headerStyles.background,
          color: headerStyles.color,
          pt: {
            xs:
              headerStyle === "minimal"
                ? "calc(env(safe-area-inset-top, 0px) + 8px)"
                : "calc(env(safe-area-inset-top, 0px) + 16px)",
            sm: headerStyle === "minimal" ? 1.5 : 3,
          },
          pr: { xs: 2, sm: 3 },
          pb: headerStyle === "minimal" ? { xs: 0.5, sm: 0.5 } : { xs: 2, sm: 3 },
          pl: { xs: 2, sm: 3 },
          position: "relative",
          zIndex: 1,
          flexShrink: 0,
          ...(isMobile && {
            position: "sticky",
            top: 0,
          }),
        }}
      >
        <IconButton
          autoFocus
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: {
              xs: "calc(env(safe-area-inset-top, 0px) + 8px)",
              sm: 16,
            },
            right: 16,
            color: headerStyles.closeOnDark
              ? "text.secondary"
              : "common.white",
            bgcolor: headerStyles.closeOnDark
              ? alpha(theme.palette.common.black, 0.04)
              : alpha(theme.palette.common.white, 0.1),
            "&:hover": {
              bgcolor: headerStyles.closeOnDark
                ? alpha(theme.palette.common.black, 0.08)
                : alpha(theme.palette.common.white, 0.2),
            },
          }}
        >
          <Close />
        </IconButton>

        {title && (
          <Typography
            variant="h5"
            fontWeight={headerStyles.titleWeight}
            sx={{
              mb: 1,
              pr: 4,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography
            variant="body2"
            sx={{ opacity: 0.9, fontSize: { xs: "0.9rem", sm: "1rem" } }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <DialogContent
        sx={
          [
            {
              p: 0,
              bgcolor:
                headerStyle === "minimal" ? "background.paper" : "grey.50",
              overflowX: "hidden",
              overflowY: footer ? "hidden" : "auto",
              maxHeight: isMobile ? "none" : "calc(95vh - 180px)",
              minHeight: footer ? 0 : isMobile ? "auto" : "500px",
              flex: 1,
              display: footer ? "flex" : undefined,
              flexDirection: footer ? "column" : undefined,
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
            },
            ...(dialogContentSx ? [dialogContentSx] : []),
          ] as SxProps<Theme>
        }
      >
        {footer ? (
          <>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Box
                sx={
                  (contentSx
                    ? [bodyPaddingSx, contentSx]
                    : bodyPaddingSx) as SxProps<Theme>
                }
              >
                {children}
              </Box>
            </Box>
            <Box
              sx={
                (footerSx
                  ? [footerPaddingBaseSx, footerSx]
                  : footerPaddingBaseSx) as SxProps<Theme>
              }
            >
              {footer}
            </Box>
          </>
        ) : (
          <Box
            sx={
              (contentSx
                ? [bodyPaddingSx, contentSx]
                : bodyPaddingSx) as SxProps<Theme>
            }
          >
            {children}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActionModal;
